import Header from '../Header/Header';
import { CustomTable, SuccessModal, Pagination, Search, Loader, ICONS, Icon, Select } from '@ohif/ui';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '@custom/Utils/config/apiRequest';
import { _commonUrls } from '@custom/Utils/constants/apiUrls';
import { servicesManager } from '../../app/src/App';
import { calculateAge, capitalizeWords, customStyle, getCurrencyContact, getCurrencySymbol } from '@custom/Utils/utils';
import { useTheme } from "../../ui/src/contextProviders";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import getInvoiceApi, { InvoiceFilterParams } from '@custom/rtk-apis/billing/invoice/getInvoice';
import { Form, Formik } from 'formik';
import getBillingGeneralSettings from '@custom/rtk-apis/billing/getGeneralSettings';

const BillingInvoice = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const invoiceData = useSelector((state: RootState) => state?.getInvoiceResponse);
  const invoiceByIdData = useSelector((state: RootState) => state?.getInvoiceByIdResponse);
  const billingGeneralSettingsData = useSelector((state: RootState) => state.getBillingGeneralSettingsSliceResponse);

  const { isDarkMode } = useTheme();
  const { uiNotificationService } = servicesManager.services;

  const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
  const [successModalText, setSuccessModalText] = useState('');
  const [load, setLoad] = useState(true);
  const [selectedAppointmentId, setSelectedAppointmentId] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [search, setSearch] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalItems, setTotalItems] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const buttonRef = useRef(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filterValue, setFilterValue] = useState({
    status: [],
    paymentMethod: [],
    cancelItems: false,
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [currency, setCurrency] = useState("");

  useEffect(() => {
    const initializeData = async () => {
      await fetchCommonData();
      await fetchInvoiceData();
      await dispatch(getBillingGeneralSettings());
    };
    initializeData();
  }, [dispatch]);

  useEffect(() => {
    if (billingGeneralSettingsData?.result?.currency) {
      setCurrency(billingGeneralSettingsData?.result?.currency);
    }
  }, [billingGeneralSettingsData]);

  const defaultCountryCode = billingGeneralSettingsData?.status && billingGeneralSettingsData?.result?.currency ? getCurrencyContact(billingGeneralSettingsData.result.currency) : '+91';

  const prepareFilterParams = (filterValues: {
    status: string[] | { value: string; label: string }[];
    paymentMethod: string[] | { value: string; label: string }[];
    cancelItems: boolean;
  },
  pgNumber?: number,
  pgLimit?: number,
  search?: string): InvoiceFilterParams => {
    const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');
    const isFilterEmpty =
      filterValues.status.length === 0 &&
      filterValues.paymentMethod.length === 0 &&
      !filterValues.cancelItems;

    if (isFilterEmpty) {
      return { 
        hospitalId, 
        limit: pgLimit, 
        offset: pgLimit * (pgNumber - 1), 
        search: search || '' 
      };
    }

    const statuses = filterValues.status.map(s => (typeof s === 'string' ? s : s.value));
    const paymentMethods = filterValues.paymentMethod.map(p => (typeof p === 'string' ? p : p.value));

    const params: InvoiceFilterParams = {
      hospitalId,
      limit: pgLimit,
      offset: pgLimit * (pgNumber - 1), 
      search: search || '',
    };

    const invoiceStatuses = statuses.filter(s => s === 'Paid' || s === 'Unpaid' || s === 'Subsidy');
    if (invoiceStatuses.length > 0) {
      params.invoiceStatus = invoiceStatuses;
    }

    if (invoiceStatuses.includes('Paid')) {
      params.statusOfBill = ['Paid'];
    }

    const refundStatuses = statuses.filter(s => s === 'Refund');
    const refundUnpaidStatuses = statuses.filter(s => s === 'Refund (Unpaid)');

    if (refundStatuses.length > 0 && refundUnpaidStatuses.length === 0) {
      params.statusOfBill = params.statusOfBill
        ? Array.from(new Set([...params.statusOfBill, 'Refund']))
        : ['Refund'];
    } else if (refundUnpaidStatuses.length > 0 && refundStatuses.length === 0) {
      params.statusOfBill = params.statusOfBill
        ? Array.from(new Set([...params.statusOfBill, 'Refund (Unpaid)']))
        : ['Refund (Unpaid)'];
    } else if (filterValues.cancelItems && refundStatuses.length === 0 && refundUnpaidStatuses.length === 0) {
      params.statusOfBill = params.statusOfBill
        ? Array.from(new Set([...params.statusOfBill, 'Refund']))
        : ['Refund'];
    } else if (refundStatuses.length > 0 && refundUnpaidStatuses.length > 0) {
      params.statusOfBill = params.statusOfBill
        ? Array.from(new Set([...params.statusOfBill, 'Refund', 'Refund (Unpaid)']))
        : ['Refund', 'Refund (Unpaid)'];
    }

    if (paymentMethods.length > 0 && !filterValues.cancelItems && refundStatuses.length === 0 && refundUnpaidStatuses.length === 0) {
      params.modeOfPayment = paymentMethods;
    }
    if (paymentMethods.length > 0 && (filterValues.cancelItems || refundStatuses.length > 0 || refundUnpaidStatuses.length > 0)) {
      params.modeOfRefund = paymentMethods;
    }
    return params;
  };

  const fetchCommonData = async () => {
    try {
      const commonData = await apiRequest({ method: 'POST', endUrl: _commonUrls.list, body: {}, headerType: 'json' });
      const methods = commonData?.result?.paymentMethods || [];
      setPaymentMethods(methods.map(method => ({
        value: method.value,
        label: method.label,
      })));
    } catch (error) {
      console.error("Error fetching payment methods:", error);
    }
  };

  const fetchInvoiceData = async () => {
    setLoad(true);
    const filterParams = prepareFilterParams(filterValue, pgNumber, pgLimit, search);
    try {
      await dispatch(getInvoiceApi(filterParams)).unwrap();
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice data',
        type: 'error',
      });
    } finally {
      setLoad(false);
    }
  };

  const reportStatusList = [
    { value: 'Unpaid', label: 'Unpaid' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Subsidy', label: 'Subsidy' },
    { value: 'Refund', label: 'Refund (Paid)' },
    { value: 'Refund (Unpaid)', label: 'Refund (Unpaid)' },
  ];

  const handleSaveForm = async (values: {
    status: string[];
    paymentMethod: { value: string; label: string }[];
    cancelItems: boolean;
  }) => {
    const newFilterValue = {
      status: values.status || [],
      paymentMethod: values.paymentMethod || [],
      cancelItems: values.cancelItems || false,
    };

    setFilterValue(newFilterValue);
    setPgNumber(1);
    setIsFilterOpen(false);

    const selectedStatusCount = values.status ? values.status.length : 0;
    const selectedPaymentMethodCount = values.paymentMethod ? values.paymentMethod.length : 0;
    setSelectedCount(selectedStatusCount + selectedPaymentMethodCount);

    const filterParams = prepareFilterParams(newFilterValue, 1, pgLimit, search);
    try {
      await dispatch(getInvoiceApi(filterParams)).unwrap();
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to apply filters',
        type: 'error',
      });
    }
  };

  const resetFilter = async () => {
    const resetValues = {
      status: [],
      paymentMethod: [],
      cancelItems: false,
    };
    setFilterValue(resetValues);
    setIsFilterOpen(false);
    setSelectedCount(0);
    setPgNumber(1);
    setSearch('');
    setSearchInputValue('');

    const filterParams = prepareFilterParams(resetValues, 1, pgLimit, '');
    try {
      await dispatch(getInvoiceApi(filterParams)).unwrap();
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to reset filters',
        type: 'error',
      });
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '--';
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds} ${ampm}`;
  };

  useEffect(() => {
    if (invoiceData?.status === 'success' && invoiceData?.result) {
      const result = Array.isArray(invoiceData.result) ? invoiceData.result : [invoiceData.result];
      let formattedData = result.flatMap(item => {
        const patientInfo = item.patientInfo || {};
        const isCanceled = item.type === 'CANCELED_INVOICE';
        const statusOfBill = isCanceled
          ? item?.canceledBillDetails?.statusOfBill || 'Unpaid'
          : item?.invoiceStatus || 'Unpaid';

          if (
            item?.invoiceStatus === 'Paid' &&
            item?.canceledBillDetails?.statusOfBill === 'Cancelled' &&
            item?.status === 'CANCELLED' &&
            item?.type === 'CANCELED_INVOICE'
          ) {
            return [];
          }

        const displayStatus = statusOfBill === 'Paid' && item?.status === 'CANCELLED' && item?.canceledBillDetails?.statusOfBill === 'Cancelled' ? 'Paid (Cancelled)' : statusOfBill === 'Refund' ? 'Refund (Paid)' : statusOfBill === 'Unpaid' && item?.status === 'CANCELLED' ? 'Unpaid (Cancelled)' : statusOfBill;

        const getPaymentMethod = (status) => {
          let rawMethod;
          switch (status) {
            case 'Paid':
              rawMethod = item?.modeOfPayment;
              break;
            case 'Refund':
            case 'Refund (Unpaid)':
              rawMethod = item?.canceledBillDetails?.modeOfRefund;
              break;
            case 'Unpaid':
              return '--';
            default:
              return '--';
          }

          if (rawMethod === 'PayTM') return 'PayTM';
          if (typeof rawMethod === 'string') {
            return capitalizeWords(rawMethod.replace(/([a-z])([A-Z])/g, '$1 $2')) || '--';
          }
          if (rawMethod && typeof rawMethod === 'object' && rawMethod.method) {
            return capitalizeWords(rawMethod.method.replace(/([a-z])([A-Z])/g, '$1 $2')) || '--';
          }
          return '--';
        };

        const getDateForStatus = (status) => {
          switch (status) {
            case 'Paid':
            case 'Unpaid':
            case 'Subsidy':
              return formatTimestamp(item?.createdAt) || '--';
            case 'Refund':
            case 'Refund (Unpaid)':
              return formatTimestamp(item?.canceledBillDetails?.canceledAt) || '--';
            default:
              return '--';
          }
        };

        const getAmountForStatus = (status) => {
          if (status === 'Paid' && !isCanceled) {
            return `${getCurrencySymbol(currency)}${Number(item?.totalPriceAfterDiscount).toFixed(2) || '--'}`;
          } else if (item?.invoiceStatus === 'Subsidy') {
            if (item?.totalPriceAfterDiscount != null) {
              return `${getCurrencySymbol(currency)}${Number(item.totalPriceAfterDiscount).toFixed(2)}`;
            } else if (item?.totalBill != null) {
              return `${getCurrencySymbol(currency)}${Number(item.totalBill).toFixed(2)}`;
            } else {
              return '--';
            }
          } else if (status === 'Refund') {
            const amount = (item?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice) || 0;
            return `${getCurrencySymbol(currency)}${Number(amount).toFixed(2) || '--'}`;
          } else if (status === 'Refund (Unpaid)') {
            const amount = (item?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice) || 0;
            return `${getCurrencySymbol(currency)}${Number(amount).toFixed(2) || '--'}`;
          }
          else if (status === 'Refund (Unpaid)') {
            let amount;
            if (
              (status === 'Refund (Unpaid)') &&
              (item?.canceledBillDetails?.statusOfBill === 'Refund (Unpaid)') &&
              item?.canceledBillItems?.length === 0
            ) {
              if (item?.removeUrgencyCharges?.value) {
                amount = item.removeUrgencyCharges.value;
              } else {
                amount = item?.totalPriceAfterDiscount || 0;
              }
            } else if (status === 'Refund (Unpaid)' && item?.canceledBillDetails?.statusOfBill === 'Refund (Unpaid)') {
              const isAllEmpty = (
                (!item?.hasOwnProperty('modality') || item?.modality === "" || item?.modality === undefined) &&
                item?.extraModalities?.length === 0 &&
                item?.serviceInvestigation?.length === 0
              );
              amount = isAllEmpty
                ? (item?.totalPriceAfterDiscount || 0)
                : (item?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice || 0);
            } else if (status === 'Refund' && item?.canceledBillDetails?.statusOfBill === 'Refund' && item?.status === 'CANCELLED') {
              if (item?.canceledBillItems?.length === 0 && item?.removeUrgencyCharges?.value) {
                amount = item.removeUrgencyCharges.value;
              } else {
                const isAllEmpty = (
                  (!item?.hasOwnProperty('modality') || item?.modality === "" || item?.modality === undefined) &&
                  item?.extraModalities?.length === 0 &&
                  item?.serviceInvestigation?.length === 0
                );
                amount = isAllEmpty
                  ? (item?.totalPriceAfterDiscount || 0)
                  : (item?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice || 0);
              }
            } else {
              const isAllEmpty = (
                (!item?.hasOwnProperty('modality') || item?.modality === "" || item?.modality === undefined) &&
                item?.extraModalities?.length === 0 &&
                item?.serviceInvestigation?.length === 0
              );
              amount = status === 'Refund' && isAllEmpty
                ? (item?.totalPriceAfterDiscount || 0)
                : (item?.currentCanceledBillItems?.[0]?.canceledBillDetails?.totalCanceledPrice || 0);
            }
            return `${getCurrencySymbol(currency)}${Number(amount).toFixed(2) || '--'}`;
          } else if (status === 'Unpaid') {
            return `${getCurrencySymbol(currency)}${Number(item?.totalBill).toFixed(2) || '--'}`;
          } else {
            return '--';
          }
        };

        const shouldHighlightAndDisableCancel =
          item?.status === 'CANCELLED' &&
          displayStatus === 'Paid' &&
          item?.canceledBillDetails?.statusOfBill === 'Cancelled';

        const disStatus = item?.discountRequest?.status || '--';
        console.log('item', item?.discountRequest?.status);

        return {
          _id: item?._id || '--',
          name: `${capitalizeWords(patientInfo?.firstName) || ''} ${capitalizeWords(patientInfo?.lastName) || ''}`.trim() || '--',
          age: patientInfo?.dob ? calculateAge(patientInfo?.dob) : '--',
          gender: patientInfo?.gender || '--',
          patientId: patientInfo?.patientId || '--',
          uhid: patientInfo?.UHID || '--',
          phoneNumber: patientInfo?.contactInfo?.mobileNo
            ? `${patientInfo?.contactInfo?.dialCode ? patientInfo?.contactInfo?.dialCode + ' ' : ''}${patientInfo?.contactInfo?.mobileNo}`
            : '--',
          invoiceNumber: item?.invoiceNumber?.replace(/-/g, ''),
          appointmentId: item?.appointmentId,
          status: displayStatus,
          discountStatus: disStatus,
          paymentMethod: getPaymentMethod(statusOfBill),
          createdAt: getDateForStatus(statusOfBill),
          originalCreatedAt: item?.createdAt,
          amount: getAmountForStatus(statusOfBill),
          canceledBillItems: item?.canceledBillItems || [],
          appointmentCancelStatus: item?.status,
          shouldDisableCancel: shouldHighlightAndDisableCancel,
          extraModalities: item?.extraModalities || [],
          serviceInvestigation: item?.serviceInvestigation || [],
          modality: item?.items?.[0]?.name || '',
        };
      });

      const uniqueMap = new Map();
      formattedData.forEach(row => {
        const key = `${row.invoiceNumber}-${row.status}`;
        const existing = uniqueMap.get(key);
        if (!existing || (existing.amount === '--' && row.amount !== '--')) {
          uniqueMap.set(key, row);
        }
      });
      const uniqueData = Array.from(uniqueMap.values());

      setTableData(uniqueData);
      setFilteredTableData(uniqueData);
      setTotalItems(invoiceData?.pagination?.totalCount);
      setLoad(false);
    } else if (invoiceData?.status === 'error') {
      uiNotificationService.show({
        title: 'Error',
        message: invoiceData?.msg || 'Failed to fetch invoice data',
        type: 'error',
      });
      setTableData([]);
      setFilteredTableData([]);
      setTotalItems(0);
      setLoad(false);
    }
  }, [invoiceData, uiNotificationService, filterValue.status, filterValue.cancelItems, currency]);

  useEffect(() => {
    const handleInvoiceByIdResponse = async () => {
      if (invoiceByIdData?.status === 'success' && invoiceByIdData?.result && selectedAppointmentId) {
        navigate(`/invoice/preview/${selectedAppointmentId}`, {
          state: {
            fromInvoiceScreen: true,
            invoiceDetails: invoiceByIdData.result,
            isEditMode: invoiceByIdData.isEditMode,
          },
        });
        setSelectedAppointmentId(null);
      } else if (invoiceByIdData?.status === 'error') {
        uiNotificationService.show({
          title: 'Error',
          message: invoiceByIdData?.msg || 'Failed to fetch invoice details',
          type: 'error',
        });
        setSelectedAppointmentId(null);
      }
    };

    handleInvoiceByIdResponse();
  }, [invoiceByIdData, navigate, selectedAppointmentId, uiNotificationService]);

  const onPageChangeHandler = (pg: number) => {
    setPgNumber(pg);
    const filterParams = prepareFilterParams(filterValue, pg, pgLimit, search);
    dispatch(getInvoiceApi(filterParams)).unwrap().catch(error => {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice data for new page',
        type: 'error',
      });
    });
  };

  const handlePageSizeChange = (e: number) => {
    setPgLimit(e);
    setPgNumber(1);
    const filterParams = prepareFilterParams(filterValue, 1, e, search);
    dispatch(getInvoiceApi(filterParams)).unwrap().catch(error => {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice data with new page size',
        type: 'error',
      });
    });
  };

  const handlePreview = async (item: { appointmentId: string; invoiceStatus: string; _id: string; canceledBillItems?: any[] }) => {
    setSelectedAppointmentId(item.appointmentId);
    try {
      navigate(`/invoice/preview/${item._id}`, {
        state: {
          fromInvoiceScreen: true,
          invoiceDetails: invoiceByIdData?.result,
          isEditMode: false,
          currentCanceledBillItems: item.canceledBillItems || [],
          tableData: item,
        },
      });
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice for preview',
        type: 'error',
      });
    }
  };

  const handleEdit = async (item: { appointmentId: string; _id: string }) => {
    setSelectedAppointmentId(item.appointmentId);
    try {
      navigate(`/invoice/edit/${item._id}`, {
        state: {
          fromInvoiceScreen: true,
          invoiceDetails: {
            ...invoiceByIdData?.result,
            _id: item._id,
          },
          isEditMode: true,
        },
      });
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice for editing',
        type: 'error',
      });
    }
  };

  const handleCancel = async (item: { appointmentId: string; _id: string }) => {
    setSelectedAppointmentId(item.appointmentId);
    try {
      navigate(`/invoice/cancel/${item._id}`, {
        state: {
          fromInvoiceScreen: true,
          invoiceDetails: {
            ...invoiceByIdData?.result,
            _id: item._id,
          },
          isCancelMode: true,
          tableData: item,
        },
      });
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice for cancellation',
        type: 'error',
      });
    }
  };

  const handleRefund = async (item: { appointmentId: string; _id: string; canceledBillItems?: any[] }) => {
    setSelectedAppointmentId(item.appointmentId);
    try {
      navigate(`/invoice/refund/${item._id}`, {
        state: {
          fromInvoiceScreen: true,
          invoiceDetails: {
            ...invoiceByIdData?.result,
            _id: item._id,
          },
          isCancelMode: false,
          isRefundDetailsOnly: true,
          currentCanceledBillItems: item.canceledBillItems || [],
          tableData: item,
        },
      });
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice for cancellation',
        type: 'error',
      });
    }
  };

  const handleCopyBillPreview = async (item: { appointmentId: string; invoiceStatus: string; _id: string }) => {
    setSelectedAppointmentId(item.appointmentId);
    try {
      navigate(`/invoice/preview/${item._id}`, {
        state: {
          fromInvoiceScreen: true,
          invoiceDetails: {
            ...invoiceByIdData?.result,
            invoiceStatus: item.invoiceStatus,
            _id: item._id,
          },
          isEditMode: false,
          isCopyBillPreview: true,
        },
      });
    } catch (error) {
      uiNotificationService.show({
        title: 'Error',
        message: 'Failed to fetch invoice for copy bill preview',
        type: 'error',
      });
    }
  };

  const columns = [
    { key: 'name', title: 'Patient Name', sort: true },
    { key: 'age', title: 'Age' },
    { key: 'gender', title: 'Gender' },
    { key: 'patientId', title: 'Patient ID' },
    { key: 'uhid', title: 'UHID' },
    { key: 'phoneNumber', title: 'Contact Number' },
    { key: 'invoiceNumber', title: 'Invoice Number' },
    { key: 'status', title: 'Status' },
    { key: 'discountStatus', title: 'Discount Status' },
    { key: 'paymentMethod', title: 'Payment Mode' },
    { key: 'createdAt', title: 'Date/Time' },
    { key: 'amount', title: 'Amount' },
    { key: 'actions', title: 'Actions' },
  ];

  const baseActions = [
    { label: 'Preview', iconName: 'preview', onClick: handlePreview },
    { label: 'Post Discount', iconName: 'edit', onClick: handleEdit },
    { label: 'Cancel', iconName: 'cancel', onClick: handleCancel },
    { label: 'Refund', iconName: 'refund', onClick: handleRefund },
    { label: 'Invoice Copy', iconName: 'copy', onClick: handleCopyBillPreview },
  ];

  const getActionsForStatus = (item) => {
    const invoiceStatus = item.status;

    switch (invoiceStatus) {
      case 'Unpaid':
        return baseActions.filter(action =>
          action.label === 'Preview' || action.label === 'Post Discount'
        );
      case 'Paid':
        return baseActions.filter(action =>
          (action.label === 'Preview' || action.label === 'Cancel' || action.label === 'Invoice Copy')
        );
      case 'Paid (Cancelled)':
        return baseActions.filter(action =>
          (action.label === 'Preview' || action.label === 'Invoice Copy')
        );
      case 'Unpaid (Cancelled)':
        return baseActions.filter(action =>
          (action.label === 'Preview')
        );
      case 'Refund (Paid)':
        return baseActions.filter(action => action.label === 'Preview');
      case 'Refund (Unpaid)':
        return baseActions.filter(action => action.label === 'Refund');
      case 'Subsidy':
        return baseActions.filter(action => (action.label === 'Preview'));
      default:
        return [];
    }
  };

  const searchClick = (searchText: string) => {
    setSearch(searchText);
    setSearchInputValue(searchText);
    setPgNumber(1);

    const filtered = tableData.filter(item =>
      item.name.toLowerCase().includes(searchText.toLowerCase()) ||
      item.phoneNumber.includes(searchText) ||
      item.patientId.includes(searchText)
    );
    setFilteredTableData(filtered);
    setTotalItems(filtered.length);
  };

  const paginatedData = filteredTableData;

  return (
    <>
      <div className='page page__fullBody' style={
        isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
      }>
        <Header title={'Billing Invoice'} isBack={false} />
        <div className='page__body'>
          <div className='tableWrap'>
            <div className='tableWrap__head flex justify-between'>
              <div className="formRow formRow__3 m_0 w_100 formRow--flexEnd">
                <div className='formCol'>
                  <Search
                    text="Patient Name, Contact Number, Patient ID"
                    onSearchClick={searchClick}
                    value={searchInputValue}
                    setValue={setSearchInputValue}
                  />
                </div>
                <div className="formCol">
                  <div className='btn__group relative'>
                    <button
                      ref={buttonRef}
                      onClick={() => setIsFilterOpen(!isFilterOpen)}
                      className="filter-button relative"
                    >
                      <img src={ICONS.filterIcon} alt="down arrow" /> Filter
                    </button>
                    {selectedCount > 0 && (
                    <span className="badge absolute top-0 left-0 translate-x-1/2 translate-y-1/2">
                      {selectedCount}
                    </span>
                    )}
                    {isFilterOpen && (
                      <div
                        className="filter-popup"
                        style={{
                          position: 'absolute',
                          top: buttonRef.current.offsetHeight + 10,
                          left: 0,
                          zIndex: 100,
                          backgroundColor: "#ffffff",
                          border: '1px solid #ccc',
                          padding: '10px',
                          width: "500px",
                          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                        }}
                      >
                        <Formik
                          initialValues={{
                            status: filterValue.status || [],
                            paymentMethod: filterValue.paymentMethod || [],
                            cancelItems: filterValue.cancelItems || false,
                          }}
                          onSubmit={(values, { setSubmitting }) => {
                            handleSaveForm(values);
                            setSubmitting(false);
                          }}
                        >
                          {({ values, setFieldValue, handleSubmit }) => (
                            <Form onSubmit={handleSubmit}>
                              <div className='formRow' style={{ height: "130px" }}>
                                <div className='formCol' style={{ width: "100%" }}>
                                  <label htmlFor='status' className="filterPopupLabel mt-3">Status</label>
                                  <Select
                                    id='status'
                                    placeholder='Select'
                                    className='custom-bg-white'
                                    isMulti={true}
                                    isClearable={false}
                                    isSearchable={true}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    options={reportStatusList}
                                    value={values.status}
                                    onChange={(selectedOptions) => {
                                      const hasRefund = selectedOptions?.some(option =>
                                        (typeof option === 'string' ? option : option.value) === 'Refund'
                                      );
                                      setFieldValue('status', selectedOptions || []);
                                      setFieldValue('cancelItems', hasRefund || false);
                                    }}
                                  />
                                </div>
                                <div className='formCol' style={{ width: "100%" }}>
                                  <label htmlFor='paymentMethod' className="filterPopupLabel mt-3">
                                    Payment Method
                                  </label>
                                  <Select
                                    id='paymentMethod'
                                    placeholder='Select'
                                    className='custom-bg-white'
                                    isMulti={true}
                                    isClearable={false}
                                    isSearchable={true}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    options={paymentMethods}
                                    value={values.paymentMethod}
                                    onChange={(selectedOptions) => {
                                      setFieldValue('paymentMethod', selectedOptions || []);
                                    }}
                                  />
                                </div>
                              </div>
                              <div className='filter-bottom'>
                                <button type="button" className='mr-2 custom-button' onClick={resetFilter}>Clear</button>
                                <button type="submit" className='custom-button main-theme'>
                                  Apply
                                </button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    )}
                    <button onClick={resetFilter} className="filter-button">
                      <Icon name="ClearFilters" className="text-black cursor-pointer" width="16" height="16"></Icon>
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div className='formCol'>
                <button className='btn btn__white' style={{ borderRadius: '50px' }} onClick={() => {
                  navigate('/billingAudit');
                }}>
                  Billing Audit Logs
                </button>
              </div>
            </div>
            <div className='tableContainer'>
              <CustomTable
                columns={columns}
                data={paginatedData}
                actions={(item) => getActionsForStatus(item)}
                searchQuery={searchQuery}
                from="billingInvoice"
                userDetails={''}
                setClickedItem={''}
                clickedItem={''}
              />
            </div>
          </div>
        </div>
        {filteredTableData.length > 0 && (
          <Pagination
            onChangePage={onPageChangeHandler}
            onChangePerPage={handlePageSizeChange}
            totalPage={totalItems}
            currentPage={pgNumber}
            perPage={pgLimit}
          />
        )}
      </div>
      <SuccessModal
        isSuccessModalOpen={isSuccessModalOpen}
        title={successModalText}
        handleCloseModal={() => setIsSuccessModal(false)}
      />
      <Loader isLoaderOpen={load || invoiceData?.loading || invoiceByIdData?.loading} />
    </>
  );
};

export default BillingInvoice;
